import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { adminApi, errMsg, fmtDate, inr } from "@/lib/api";
import { useSite } from "@/lib/store";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Btn, PageHead, Panel, inputCls } from "@/admin/ui";

const PAY = {
  paid: "bg-emerald-100 text-emerald-800", pending: "bg-amber-100 text-amber-800", expired: "bg-zinc-200 text-zinc-600",
};
export const PayBadge = ({ s }) => <span className={`text-[10px] font-bold uppercase px-2 py-0.5 font-arial ${PAY[s]}`}>{s === "pending" ? "unpaid" : s}</span>;

const CHOICE = { refund: "wants a refund", wait: "will wait", alternate: "wants an alternate" };
function ItemBadge({ i }) {
  if (i.status === "not_processed") return <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-red-100 text-red-700 font-arial" data-testid={`item-status-${i.sku}`}>Not processed{i.choice ? ` · customer ${CHOICE[i.choice]}${i.choice_note ? `: ${i.choice_note}` : ""}` : " · awaiting reply"}</span>;
  if (i.status === "refunded") return <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-zinc-200 text-zinc-700 font-arial" data-testid={`item-status-${i.sku}`}>Refunded</span>;
  return null;
}

function ItemActions({ order, sel, setSel, notify, note, onSaved }) {
  const [busy, setBusy] = useState(false);
  const run = async (status) => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/orders/${order.id}/items`, { product_ids: sel, status, notify, note });
      toast.success(status === "not_processed" ? (r.data.email_requested ? (r.data.emailed ? "Marked not processed and customer emailed with refund / wait / alternate options" : "Marked not processed, but the email could not be sent") : "Marked not processed (no email)") : "Items updated");
      setSel([]);
      onSaved(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid="order-item-actions">
      <span className="text-xs text-zinc-500">{sel.length} item(s) selected:</span>
      <Btn variant="red" className="h-8 px-3" disabled={!sel.length || busy} onClick={() => run("not_processed")} data-testid="items-mark-not-processed">Mark not processed</Btn>
      <Btn variant="outline" className="h-8 px-3" disabled={!sel.length || busy} onClick={() => run("ok")} data-testid="items-mark-processed">Back to processed</Btn>
      <Btn variant="outline" className="h-8 px-3" disabled={!sel.length || busy} onClick={() => run("refunded")} data-testid="items-mark-refunded">Mark refunded</Btn>
    </div>
  );
}

function TrackingBox({ order, onSaved }) {
  const [t, setT] = useState({ courier: order.courier || "", tracking_number: order.tracking_number || "", tracking_url: order.tracking_url || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      onSaved((await adminApi.post(`/orders/${order.id}/tracking`, t)).data);
      toast.success("Tracking details saved – they'll appear in the next status email as {tracking}");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const f = (k, ph) => <input value={t[k]} onChange={(e) => setT({ ...t, [k]: e.target.value })} placeholder={ph} className={inputCls} data-testid={`order-tracking-${k}`} />;
  return (
    <div className="bg-zinc-50 border border-zinc-200 p-4 space-y-3" data-testid="order-tracking">
      <p className="font-bold text-sm">Courier tracking</p>
      <div className="grid sm:grid-cols-2 gap-2">{f("courier", "Courier (e.g. Delhivery)")}{f("tracking_number", "Tracking / AWB number")}</div>
      {f("tracking_url", "Tracking link (https://…)")}
      <Btn variant="outline" className="h-8 px-3" disabled={busy} onClick={save} data-testid="order-tracking-save">Save tracking</Btn>
    </div>
  );
}

const ADDR_FIELDS = [["line1", "Address line 1"], ["line2", "Address line 2"], ["city", "City"], ["state", "State"], ["postal_code", "PIN / postal code"], ["country", "Country"]];
const addrText = (a) => a && <>{a.name && <>{a.name}<br /></>}{a.line1}{a.line2 && `, ${a.line2}`}<br />{a.city}{a.state && `, ${a.state}`} {a.postal_code}<br />{a.country}</>;

function AddrInputs({ a, set, prefix, withName }) {
  const fields = withName ? [["name", "Name on bill"], ...ADDR_FIELDS] : ADDR_FIELDS;
  return (
    <div className="grid grid-cols-2 gap-2">
      {fields.map(([k, l]) => (
        <input key={k} value={a[k] || ""} placeholder={l} onChange={(e) => set({ ...a, [k]: e.target.value })}
          className={`${inputCls} ${["line1", "line2", "name"].includes(k) ? "col-span-2" : ""}`} data-testid={`${prefix}-${k}`} />
      ))}
    </div>
  );
}

function PeopleEdit({ order, onSaved, onCancel }) {
  const [c, setC] = useState(order.customer);
  const [a, setA] = useState(order.address);
  const [same, setSame] = useState(order.billing_same !== false);
  const [b, setB] = useState(order.billing_address || { name: order.customer.name, ...order.address });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      onSaved((await adminApi.put(`/orders/${order.id}/customer`, { customer: c, address: a, billing_same: same, billing_address: same ? null : b })).data);
      toast.success("Customer details saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid gap-4 text-sm border border-zinc-200 p-4" data-testid="order-people-edit">
      <div className="grid grid-cols-2 gap-2">
        <p className="col-span-2 label-caps text-[10px] text-zinc-500">Customer</p>
        {[["name", "Name"], ["email", "Email"], ["phone", "Phone"]].map(([k, l]) => (
          <input key={k} value={c[k]} placeholder={l} onChange={(e) => setC({ ...c, [k]: e.target.value })} className={`${inputCls} ${k === "name" ? "col-span-2" : ""}`} data-testid={`order-edit-${k}`} />
        ))}
      </div>
      <div><p className="label-caps text-[10px] text-zinc-500 mb-2">Delivery address</p><AddrInputs a={a} set={setA} prefix="order-edit-ship" /></div>
      <label className="flex items-center gap-2"><Switch checked={same} onCheckedChange={setSame} data-testid="order-edit-billing-same" /> Billing address same as delivery</label>
      {!same && <div><p className="label-caps text-[10px] text-zinc-500 mb-2">Billing address</p><AddrInputs a={b} set={setB} prefix="order-edit-bill" withName /></div>}
      <div className="flex gap-2 justify-end">
        <Btn variant="outline" onClick={onCancel}>Cancel</Btn>
        <Btn variant="red" disabled={busy} onClick={save} data-testid="order-edit-save">{busy ? "Saving…" : "Save details"}</Btn>
      </div>
    </div>
  );
}

const srcText = (s) => s && [s.source && s.source !== "(direct)" ? s.source : "", s.medium, s.campaign].filter(Boolean).join(" · ");

function OrderDialog({ order, onClose, onSaved }) {
  const { site } = useSite();
  const [stage, setStage] = useState(order.stage_id);
  const [notify, setNotify] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState([]);
  const paid = order.payment_status === "paid";
  const save = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/orders/${order.id}/stage`, { stage_id: stage, notify, note });
      toast.success(r.data.email_requested ? (r.data.emailed ? "Stage updated and customer emailed" : "Stage updated, but the email could not be sent") : "Stage updated (no email)");
      setNote("");
      onSaved(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const a = order.address;
  const [editing, setEditing] = useState(false);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="order-dialog">
        <DialogHeader><DialogTitle className="font-head text-2xl">{order.order_number} <PayBadge s={order.payment_status} /></DialogTitle></DialogHeader>
        {editing ? <PeopleEdit order={order} onCancel={() => setEditing(false)} onSaved={(o) => { setEditing(false); onSaved(o); }} /> : (
          <div className="grid sm:grid-cols-3 gap-5 text-sm">
            <div><p className="label-caps text-[10px] text-zinc-500 mb-1">Customer</p><p data-testid="order-customer">{order.customer.name}<br />{order.customer.email}<br />{order.customer.phone}</p></div>
            <div><p className="label-caps text-[10px] text-zinc-500 mb-1">Deliver to</p><p data-testid="order-ship-address">{addrText(a)}</p></div>
            <div><p className="label-caps text-[10px] text-zinc-500 mb-1">Billing address</p><p data-testid="order-bill-address">{order.billing_same === false && order.billing_address ? addrText(order.billing_address) : <span className="text-zinc-500">Same as delivery</span>}</p></div>
            <div className="sm:col-span-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs" data-testid="order-source"><span className="label-caps text-[10px] text-zinc-500 mr-2">Came from</span><b>{order.source?.channel || "Unknown"}</b>{srcText(order.source) && <span className="text-zinc-500"> · {srcText(order.source)}</span>}{order.source?.landing && <span className="text-zinc-400"> · landed on {order.source.landing}</span>}</p>
              <Btn variant="outline" className="h-8 px-3" onClick={() => setEditing(true)} data-testid="order-edit-customer">Edit customer details</Btn>
            </div>
          </div>
        )}
        {order.attention && (
          <div className="bg-amber-50 border border-amber-300 p-3 text-sm flex justify-between items-center gap-3" data-testid="order-attention">
            <span>{order.late_payment ? `Payment ${order.late_payment.ref} arrived after the 30-minute window expired and the records were released. Check stock, then fulfil or refund in Razorpay.` : "The customer replied about not-processed items. Refunds are done in Razorpay; then mark the items refunded."}</span>
            <Btn variant="outline" className="h-8 px-3 shrink-0" onClick={() => adminApi.post(`/orders/${order.id}/seen`).then((r) => onSaved(r.data))} data-testid="order-mark-seen">Mark as handled</Btn>
          </div>
        )}
        <ul className="border-y border-zinc-200 py-3 space-y-1.5 text-sm">
          {order.items.map((i) => (
            <li key={i.product_id} className="flex justify-between gap-3 items-start" data-testid={`order-item-${i.sku}`}>
              <span className="flex gap-2 items-start">
                {paid && <Checkbox checked={sel.includes(i.product_id)} onCheckedChange={(v) => setSel(v ? [...sel, i.product_id] : sel.filter((x) => x !== i.product_id))} className="mt-0.5" data-testid={`order-item-select-${i.sku}`} />}
                <span><span className="font-arial text-zinc-500">{i.sku}</span> · {i.artist} – {i.title} ({i.format}) × {i.qty} <ItemBadge i={i} /></span>
              </span>
              <span className="font-arial shrink-0">{inr(i.price * i.qty)}</span>
            </li>
          ))}
          {order.discount > 0 && <li className="flex justify-between text-emerald-700" data-testid="order-discount"><span>Coupon {order.coupon_code}</span><span className="font-arial">−{inr(order.discount)}</span></li>}
          <li className="flex justify-between text-zinc-500"><span>Shipping</span><span className="font-arial">{inr(order.shipping)}</span></li>
          <li className="flex justify-between font-bold"><span>Total</span><span className="font-arial">{inr(order.total)}</span></li>
        </ul>
        {paid && <ItemActions order={order} sel={sel} setSel={setSel} notify={notify} note={note} onSaved={onSaved} />}
        <TrackingBox key={order.id} order={order} onSaved={onSaved} />
        <Btn variant="ghost" className="text-red self-start" data-testid="order-delete" onClick={async () => {
          if (!window.confirm(`Permanently delete order ${order.order_number}? This can't be undone.${order.payment_status === "pending" ? " Its held stock will be released." : ""}`)) return;
          try {
            await adminApi.delete(`/orders/${order.id}`);
            toast.success("Order deleted");
            onSaved(null);
          } catch (e) {
            toast.error(errMsg(e));
          }
        }}>Delete this order</Btn>
        {order.payment?.ref && <p className="text-xs text-zinc-500 font-arial" data-testid="order-payment-ref">{order.payment.provider === "test-mode" ? "TEST payment" : "Razorpay"} ref: {order.payment.ref} · paid {fmtDate(order.paid_at)}</p>}
        <p className={`text-xs font-arial ${order.terms_accepted?.accepted ? "text-emerald-700" : "text-zinc-500"}`} data-testid="order-terms-accepted">
          {order.terms_accepted?.accepted
            ? `✓ Customer accepted the Terms and Conditions on ${fmtDate(order.terms_accepted.at)}${order.terms_accepted.ip ? ` · IP ${order.terms_accepted.ip}` : ""}`
            : "Terms and Conditions acceptance not recorded (order placed before this was added)"}
        </p>
        <div className="bg-zinc-50 border border-zinc-200 p-4 space-y-3">
          <p className="font-bold text-sm">Move to stage</p>
          {!paid && <p className="text-xs text-red" data-testid="order-unpaid-warning">This order is unpaid, so it can't be moved through the stages.</p>}
          <Select value={stage} onValueChange={setStage} disabled={!paid}>
            <SelectTrigger className="rounded-none bg-white" data-testid="order-stage-select"><SelectValue placeholder="Choose stage" /></SelectTrigger>
            <SelectContent className="rounded-none">{site?.stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note for the customer (e.g. courier & tracking no.)" className={inputCls} disabled={!paid} data-testid="order-stage-note" />
          <label className="flex items-center gap-3 text-sm"><Switch checked={notify} onCheckedChange={setNotify} disabled={!paid} data-testid="order-notify-switch" /> Email the customer about this update</label>
          <Btn onClick={save} disabled={!paid || busy || !stage} data-testid="order-stage-save">{busy ? "Updating…" : "Update stage"}</Btn>
        </div>
        <div>
          <p className="label-caps text-[10px] text-zinc-500 mb-2">History</p>
          <ul className="space-y-1.5 text-xs" data-testid="order-history">
            {[...(order.history || [])].reverse().map((h, i) => <li key={i} className="flex justify-between gap-3"><span><b>{h.stage_name}</b>{h.note && ` – ${h.note}`}{h.emailed && " · emailed"}</span><span className="text-zinc-500 font-arial shrink-0">{fmtDate(h.at)}</span></li>)}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BulkBar({ sel, onDone }) {
  const { site } = useSite();
  const [stage, setStage] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const applyStage = () => run(async () => {
    const r = (await adminApi.post("/orders/bulk-stage", { ids: sel, stage_id: stage, notify, note: "" })).data;
    toast.success(`${r.updated} order(s) updated${notify ? `, ${r.emailed} emailed` : ""}${r.skipped_unpaid ? ` · ${r.skipped_unpaid} unpaid skipped` : ""}`);
  });
  const del = () => {
    if (!window.confirm(`Permanently delete ${sel.length} order(s)? This can't be undone. Held stock on unpaid orders will be released; stock for paid orders is not put back.`)) return;
    run(async () => toast.success(`${(await adminApi.post("/orders/bulk-delete", { ids: sel })).data.deleted} order(s) deleted`));
  };
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4 bg-zinc-900 text-white px-4 py-3" data-testid="orders-bulk-bar">
      <span className="text-sm font-bold" data-testid="orders-selected-count">{sel.length} selected</span>
      <Select value={stage} onValueChange={setStage}>
        <SelectTrigger className="w-52 rounded-none bg-white text-black h-9" data-testid="orders-bulk-stage"><SelectValue placeholder="Move to stage…" /></SelectTrigger>
        <SelectContent className="rounded-none">{site?.stages.filter((s) => s.id !== "pending").map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
      </Select>
      <label className="flex items-center gap-2 text-sm"><Switch checked={notify} onCheckedChange={setNotify} data-testid="orders-bulk-notify" /> Email customers</label>
      <Btn variant="red" className="h-9 px-4" disabled={!stage || busy} onClick={applyStage} data-testid="orders-bulk-apply">Update stage</Btn>
      <Btn variant="outline" className="h-9 px-4 ml-auto text-black" disabled={busy} onClick={del} data-testid="orders-bulk-delete">Delete selected</Btn>
    </div>
  );
}

export default function Orders() {
  const [q, setQ] = useState("");
  const [payment, setPayment] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [sel, setSel] = useState([]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const pageIds = data?.items.map((o) => o.id) || [];
  const allOn = pageIds.length > 0 && pageIds.every((id) => sel.includes(id));
  const load = useCallback(() => {
    adminApi.get("/orders", { params: { q, payment: ["all", "attention"].includes(payment) ? "" : payment, attention: payment === "attention", page } }).then((r) => setData(r.data));
  }, [q, payment, page]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div>
      <PageHead title="Orders" desc="Search orders, check payment and move paid orders through your order flow.">
        <Btn variant="outline" data-testid="orders-add-samples" onClick={() => adminApi.post("/orders/samples").then((r) => { toast.success(`${r.data.created} sample orders added`); load(); }).catch((e) => toast.error(errMsg(e)))}>Add 3 sample orders</Btn>
      </PageHead>
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-zinc-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Order number, name, email or phone" className={`${inputCls} pl-9`} data-testid="orders-search" />
        </div>
        <Select value={payment} onValueChange={(v) => { setPayment(v); setPage(1); }}>
          <SelectTrigger className="w-44 rounded-none bg-white h-10" data-testid="orders-payment-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All payments</SelectItem><SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="pending">Unpaid</SelectItem><SelectItem value="expired">Expired</SelectItem><SelectItem value="attention">Customer replied</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {sel.length > 0 && <BulkBar sel={sel} onDone={() => { setSel([]); load(); }} />}
      <Panel className="p-0 sm:p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead><tr className="text-left label-caps text-[10px] text-zinc-500 border-b border-zinc-200">
            <th className="pl-4 py-3 w-8"><Checkbox checked={allOn} onCheckedChange={(v) => setSel(v ? [...new Set([...sel, ...pageIds])] : sel.filter((id) => !pageIds.includes(id)))} data-testid="orders-select-all" /></th>
            {["Order", "Date", "Customer", "Total", "Payment", "Stage"].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}
          </tr></thead>
          <tbody>
            {data?.items.map((o) => (
              <tr key={o.id} onClick={() => setOpen(o)} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`order-row-${o.order_number}`}>
                <td className="pl-4 py-3" onClick={(e) => e.stopPropagation()}><Checkbox checked={sel.includes(o.id)} onCheckedChange={() => toggle(o.id)} data-testid={`order-select-${o.order_number}`} /></td>
                <td className="px-4 py-3 font-arial font-bold">{o.order_number}{o.sample && <span className="ml-2 text-[9px] uppercase bg-zinc-200 px-1 py-0.5">Sample</span>}{o.imported && <span className="ml-2 text-[9px] uppercase bg-sky-100 text-sky-800 px-1 py-0.5">Imported</span>}</td>
                <td className="px-4 py-3 text-zinc-500 font-arial text-xs">{fmtDate(o.created_at)}</td>
                <td className="px-4 py-3">{o.customer.name}<div className="text-xs text-zinc-500">{o.customer.email}</div><div className="text-[10px] uppercase tracking-wide text-zinc-400 mt-0.5" data-testid={`order-source-${o.order_number}`}>{o.source?.channel || "Unknown"}</div></td>
                <td className="px-4 py-3 font-arial">{inr(o.total)}</td>
                <td className="px-4 py-3"><PayBadge s={o.payment_status} /></td>
                <td className="px-4 py-3">{o.stage_name}{o.attention && <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 bg-amber-100 text-amber-800" data-testid={`order-attention-${o.order_number}`}>Customer replied</span>}{o.items.some((i) => i.status === "not_processed") && !o.attention && <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 bg-red-100 text-red-700">Item not processed</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.items.length === 0 && <p className="p-10 text-center text-zinc-500" data-testid="orders-empty">No orders found.</p>}
      </Panel>
      {data?.pages > 1 && (
        <div className="flex gap-3 items-center mt-4 text-sm">
          <Btn variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Btn>
          <span>Page {page} / {data.pages}</span>
          <Btn variant="outline" disabled={page >= data.pages} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}
      {open && <OrderDialog order={open} onClose={() => setOpen(null)} onSaved={(o) => { setOpen(o); load(); }} />}
    </div>
  );
}
